import { useEffect, useMemo, useRef, useState } from "react";
import { Search, X } from "lucide-react";
import type { StickerElement } from "../../shared/model";
import { useEditor, useStyle } from "../editor/react/hooks";
import { newId } from "../editor/elements";
import { STICKER_CATEGORIES, STICKERS, stickerLabel, stickerUrl } from "../data/stickers";
import { Chip, Panel } from "../ui/kit";

export function StickerDrawer({ onClose, side }: { onClose: () => void; side: "left" | "right" }) {
  const ed = useEditor();
  const current = useStyle((s) => s.stickerKey);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState<string>("all");
  const list = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return STICKERS.filter((s) => (cat === "all" || s.cat === cat) && (!needle || stickerLabel(s.key).includes(needle) || s.cat.includes(needle)));
  }, [q, cat]);

  // one stable listener: re-subscribing on every render would drop key presses that arrive mid-update
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const a = document.activeElement as HTMLElement | null;
      if (e.key === "Escape" && !(a && (a.tagName === "INPUT" || a.tagName === "TEXTAREA") && !a.closest(".sticker-drawer"))) closeRef.current();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const pick = (key: string) => {
    ed.setStyle({ stickerKey: key });
    ed.setTool("sticker");
  };

  const dropAt = (key: string, clientX: number, clientY: number) => {
    const vp = ed.viewport;
    if (!vp) return;
    const r = vp.getBoundingClientRect();
    const world = ed.placementPoint(ed.camera.toWorld({ x: clientX - r.left, y: clientY - r.top }));
    const info = STICKERS.find((s) => s.key === key)!;
    const k = 150 / Math.max(info.w, info.h);
    const el: StickerElement = {
      id: newId(), type: "sticker", key, x: world.x - (info.w * k) / 2, y: world.y - (info.h * k) / 2, w: info.w * k, h: info.h * k,
      rot: ((Math.random() - 0.5) * 8 * Math.PI) / 180, z: "", t: Date.now()
    };
    ed.insert([el]);
  };

  return (
    <Panel className={"drawer sticker-drawer drawer-" + side} seed="stickers" tape="var(--sage)" tapeSide="right" aria-label="Sticker book">
      <div className="drawer-head">
        <h3>Sticker book</h3>
        <button type="button" className="dialog-close" onClick={onClose} aria-label="Close sticker book">
          <X size={18} />
        </button>
      </div>
      <label className="search-box">
        <Search size={16} aria-hidden="true" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="search stickers…" onKeyDown={(e) => e.stopPropagation()} />
      </label>
      <div className="chips wrap">
        <Chip active={cat === "all"} onClick={() => setCat("all")}>All</Chip>
        {STICKER_CATEGORIES.map((c) => (
          <Chip key={c.key} active={cat === c.key} onClick={() => setCat(c.key)}>
            {c.label}
          </Chip>
        ))}
      </div>
      <div className="sticker-grid">
        {list.map((s) => (
          <button
            key={s.key}
            type="button"
            className={"sticker-cell" + (current === s.key ? " active" : "")}
            onClick={() => pick(s.key)}
            aria-label={stickerLabel(s.key)}
            title={stickerLabel(s.key)}
            draggable
            onDragStart={(e) => {
              e.dataTransfer.setData("application/x-sepia-sticker", s.key);
              e.dataTransfer.effectAllowed = "copy";
            }}
            onDragEnd={(e) => {
              if (e.dataTransfer.dropEffect === "none" && document.elementFromPoint(e.clientX, e.clientY)?.closest(".viewport")) dropAt(s.key, e.clientX, e.clientY);
            }}
          >
            <img src={stickerUrl(s.key)} alt="" loading="lazy" draggable={false} />
          </button>
        ))}
        {!list.length && <p className="empty-note">No stickers match “{q}”.</p>}
      </div>
      <p className="drawer-foot">tap one, then tap the page · or drag it straight on</p>
    </Panel>
  );
}
